import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { AlertBar, Button, Label, Spinner, Input } from 'scorer-ui-kit';
import { useTranslation } from 'react-i18next';
import Popup from 'reactjs-popup';
import { RightAlignedContainer, CenterAlignedContainer, LeftButton, FieldContainer, Table, TR, TH, TD, PopupTitle, SpinnerDesign, AlertContainer, overlayStyle, CloseIcon } from '../Style';
import { getEmails, addEmail, deleteEmail } from '../apis';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import '../css/popup.css';

const PopupContainer = styled.div`
  padding: 30px;
  width: 600px;
`;

const FieldLabel = styled(Label)`
  width: max-content;
  margin-right: 15px;
`;

const TextWithTooltip = styled.div`
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AlertBarContainer = styled(AlertContainer)`
  & > div > div + div {
    width: 750px;
    overflow-wrap: break-word;
  }
`;

const InputField = styled(Input)`
    width: 425px;
`;


const THContainer = styled(TH)`
  width: 400px;
  text-align: left;
`;

interface EmailInput {
  name: string;
  email: string;
}

interface Emails {
  name: string,
  email: string,
  id: number
}
function EmailSettings() {
  const [inputs, setInputs] = useState<EmailInput>({ name: '', email: '' });
  const [emails, setEmails] = useState<Emails[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [alert, setAlert] = useState<{ key: any, message: string, type: 'success' | 'error' | 'warning' | 'info' | undefined }>({ key: new Date(), message: '', type: undefined });
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { t } = useTranslation(['EmailSettings', 'Common']);

  const getAllEmails = useCallback(() => {
    getEmails()
      .then((res) => {
        if (res.data.status_code === '0') {
          setEmails(res.data.data);
          if (res.data.data.length <= 0) {
            setAlert({ key: new Date(), message: t('noEmailsFound'), type: 'info' });
          }
          setLoading(false);
        } else {
          setAlert({ key: new Date(), message: res.data.message, type: 'error' });
        }
      })
      .catch((err) => {
        if (err.response.data.message) {
          setAlert({ key: new Date(), message: err.response.data.message, type: 'error' });
        } else {
          console.error(err);
        }
      });
  }, [t]);

  useEffect(() => {
    getAllEmails();
  }, [getAllEmails]);

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputs({ ...inputs, [event.target.name]: event.target.value });
  };

  const onAdd = () => {
    let found = false;
    if (inputs.name === '') {
      setAlert({ key: new Date(), message: t('nameFieldIsRequired'), type: 'error' });
      return;
    }
    if (inputs.email === '') {
      setAlert({ key: new Date(), message: t('emailFieldIsRequired'), type: 'error' });
      return;
    }
    // eslint-disable-next-line 
    const emailRegx = /^\w+(?:[\.-]\w+)*@\w+(?:[\.-]\w+)*(?:\.\w{2,3})+$/;
    if (!emailRegx.exec(inputs.email)) {
      setAlert({ key: new Date(), message: t('pleaseEnterValidEmailAddress'), type: 'error' });
      return;
    }

    emails.forEach((itm: any) => {
      if (itm.email === (inputs.email).trim()) {
        found = true;
      }
    });
    if (found) {
      setAlert({ key: new Date(), message: t('email') + ' \'' + inputs.email + '\' ' + t('alreadyExists'), type: 'error' });
      return;
    }

    addEmail(inputs)
      .then((res) => {
        if (res.data.status_code === '0') {
          getAllEmails();
          setTimeout(() => setAlert({ key: new Date(), message: t('email') + ' \'' + inputs.email + '\' ' + t('addedSuccessfully'), type: 'success' }), 500);
        } else {
          setAlert({ key: new Date(), message: res.data.message, type: 'error' });
        }
        setModalOpen(false);
      })
      .catch((err) => {
        if (err.response.data.message) {
          setAlert({ key: new Date(), message: err.response.data.message, type: 'error' });
        } else {
          console.error(err);
        }
      });
  };

  const onDelete = (id: number, email: string) => {
    Swal.fire({
      title: t('doYouWantToDeletedThis'),
      text: email,
      icon: 'warning',
      showCancelButton: true, 
      showCloseButton: true,
      cancelButtonColor: '#d33',
      cancelButtonText: t('Common:cancel'),
      confirmButtonColor: '#3085d6',
      confirmButtonText: t('delete'),
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        deleteEmail(id)
          .then((res) => {
            if (res.data.status_code === '0') {
              setAlert({ key: new Date(), message: t('email') + ' \'' + email + '\' ' + t('deletedSuccessfully'), type: 'success' });
              getAllEmails();
            } else {
              setAlert({ key: new Date(), message: res.data.message, type: 'error' });
            }
          })
          .catch((err) => {
            if (err.response.data.message) {
              setAlert({ key: new Date(), message: err.response.data.message, type: 'error' });
            } else {
              console.error(err);
            }
          });
      }
    });
  };

  const openModal = () => {
    setAlert({ key: new Date(), message: '', type: undefined });
    setInputs({ name: '', email: '' });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setAlert({ key: new Date(), message: '', type: undefined });
  };

  return (
    <>
      <RightAlignedContainer>
        {!loading && <Button onClick={openModal}>{t('add')}</Button>}
        <Popup
          modal
          open={modalOpen}
          onClose={closeModal}
          {...{ overlayStyle }}
          closeOnDocumentClick
        >
          <PopupContainer>
            <CloseIcon onClick={closeModal}>x</CloseIcon>
            <CenterAlignedContainer>
              <PopupTitle>{t('addEmail')}</PopupTitle>
            </CenterAlignedContainer>
            <FieldContainer>
              <FieldLabel htmlFor='name' labelText={t('name')} />
              <InputField name='name' maxLength={255} placeholder={t('enterName')} value={inputs.name} onChange={onInputChange} />
            </FieldContainer>
            <FieldContainer>
              <FieldLabel htmlFor='email' labelText={t('email')} />
              <InputField name='email' maxLength={255} type='email' placeholder={t('enterEmail')} value={inputs.email} onChange={onInputChange} />
            </FieldContainer>
            <CenterAlignedContainer>
              <LeftButton design='secondary' onClick={closeModal}>{t('Common:cancel')}</LeftButton>
              <Button onClick={onAdd}>{t('add')}</Button>
            </CenterAlignedContainer>
            {
              (alert.message !== '' && alert.type === 'error' &&
                <AlertContainer key={alert.key}>
                  <AlertBar type='error' message={alert.message} />
                </AlertContainer>
              )
            }
          </PopupContainer>
        </Popup>
      </RightAlignedContainer>
      {loading ?
        <CenterAlignedContainer>
          <Spinner size='large' styling={SpinnerDesign} />
        </CenterAlignedContainer> :
        emails.length >= 1 && <div>
          <Table>
            <tbody>
              <TR>
                <THContainer bgColor='#f5f5f0'>{t('name')}</THContainer>
                <THContainer bgColor='#f5f5f0'>{t('email')}</THContainer>
                <TH bgColor='#f5f5f0'>{t('action')}</TH>
              </TR>
              {
                emails.map(({ id, name, email }) => (
                  <TR key={id}>
                    <TD textAlign='left'><TextWithTooltip title={name}>{name}</TextWithTooltip></TD>
                    <TD textAlign='left'><TextWithTooltip title={email}>{email}</TextWithTooltip></TD>
                    <TD><Button design='danger' size='small' onClick={() => onDelete(id, email)}>{t('delete')}</Button></TD>
                  </TR>
                ))
              }
            </tbody>
          </Table>
        </div>}

      {
        (alert.message !== '' && !modalOpen &&
          <AlertBarContainer key={alert.key}>
            <AlertBar type={alert.type} message={alert.message} />
          </AlertBarContainer>
        )
      }
    </>
  );
}

export default EmailSettings;
